import {PAdbContext} from '../PA.Core/PAdbContext'
import {WB_TagRelationshipRow, WB_TagRow} from '../PA.Core/PAdbSchemaRows'
import {esWB_Tag, esWB_Tag_Relationship, esWB_Tag_Text} from '../PA.Core/PAdbSchema'
import {IHaveTags} from '../PA.Core/PA.Core.IF.Aspects'
import * as Rx from 'rxjs'
import * as _ from 'lodash'
import {TagType, TagTypes} from "../PA.Core/Basic/Constants"
import {ASync} from "../PA.Core/Basic/ASync";

export interface TagBagOptions {
    dbContext?: PAdbContext,
    TagUsage?: string,
    TagsReadOnly?: boolean
    InitialTags?: number[],
    bCanRemoveInitial?: boolean
}

var TagBagOptions_default : TagBagOptions = {
    TagUsage:      'AllTagUsage',
    TagsReadOnly:  false,
    InitialTags:   [],
    bCanRemoveInitial: true
}


export class TagBag
  implements
    IHaveTags
{

    dbContext:          PAdbContext
    _options:           TagBagOptions
    _bIHaveTagsLoaded:  boolean
    _arrWBTags:         WB_TagRow[]
    _dctTaggedByTagId:  {[id: string]: boolean}
    _TagsChanged:       Rx.BehaviorSubject<IHaveTags>
    _OnIHaveTagsLoaded: Promise<IHaveTags>
    _tagUsageId:        string

    constructor(options?: TagBagOptions) {

        this._options = {}
        _.defaults(this._options, options || {}, TagBagOptions_default)

        this._tagUsageId = this._options.TagUsage || 'AllTagUsage'

        if ('dbContext' in this._options)
            this.dbContext = this._options.dbContext;
        else
            this.dbContext = new PAdbContext({});

        this._bIHaveTagsLoaded = false
        this._arrWBTags = []
        this._dctTaggedByTagId = {}
        this._TagsChanged = new Rx.BehaviorSubject<IHaveTags>(this);
        this._OnIHaveTagsLoaded = undefined
    }

    addTag(TagId: number): Promise<void> {
        var self = this;
        return new Promise<void>(function (resolve, reject) {
            self._dctTaggedByTagId[TagId] = true;
            self.TagsChanged.next(self)
            resolve();
        })
    }

    removeTag(TagId: number): void {
        this._dctTaggedByTagId[TagId] = false;
        this.TagsChanged.next(this)
    }

    hasTag(TagId: number): boolean {
        if (TagId in this._dctTaggedByTagId)
            return (this._dctTaggedByTagId[TagId]);

        return (false);
    }

    get Tags():  WB_TagRow[] {
        var selectedTags:  WB_TagRow[] = []

        _.forEach(this.AllTags, (aTag: WB_TagRow) => {
            if (this.hasTag(aTag.cif.WB_TAG_ID)) {
                selectedTags.push(aTag)
            }
        })
        return selectedTags
    }

    get bIHaveTagsLoaded(): boolean { return this._bIHaveTagsLoaded }
    get TagsReadOnly(): boolean { return this._options.TagsReadOnly}
    get TagsChanged(): Rx.BehaviorSubject<IHaveTags> { return this._TagsChanged }
    get AllTags(): WB_TagRow[] { return this._arrWBTags }
    get UsageId(): string { return this._tagUsageId }
    get OnIHaveTagsLoaded(): Promise<IHaveTags> {
        const self = this;

        if(self.bIHaveTagsLoaded) {
            return ASync.resolve(self)
        }

        // Load all tags & relationship records, then generate list requested in tagbag.. including children
        // Check if load has already started.
        if (!_.isEmpty(this._OnIHaveTagsLoaded))
            return self._OnIHaveTagsLoaded

        this._OnIHaveTagsLoaded = new Promise<IHaveTags>((resolve, error) => {

            var findWB_TagRows: WB_TagRow[]

            self.dbContext.findAll(esWB_Tag, {})
              .then((allTags: WB_TagRow[]) => {

                  findWB_TagRows = allTags
                  return self.dbContext.findAll(esWB_Tag_Text,{})
              })
              .then((allTagText) => {
                  // Now get all relationship records
                  return self.dbContext.findAll(esWB_Tag_Relationship,{"DELETED_IND": {neq: '1'}})
              })
              .then((allTagRels: WB_TagRelationshipRow) => {
                // if there are no filters, then we are done...
                  if (_.isEmpty(self._options.TagUsage)) {

                      self._arrWBTags = findWB_TagRows
                      self._bIHaveTagsLoaded = true
                      self._OnIHaveTagsLoaded = undefined
                      resolve(self)
                      return
                  }

                  var dctWBTag: {[id: number]: WB_TagRow} = {}
                  self.addRecursively(findWB_TagRows, TagTypes.TagUsage.TypeId, [self._options.TagUsage], dctWBTag)

                  var values: WB_TagRow[] = _.values(dctWBTag)
                  self._arrWBTags = values
                  self._bIHaveTagsLoaded = true
                  self._OnIHaveTagsLoaded = undefined
                  resolve(self)
              })
              .catch((err) => {
                self._OnIHaveTagsLoaded = undefined
                error(err)
              })
        })

        return this._OnIHaveTagsLoaded
    }

    addRecursively(srcTags: WB_TagRow[], sTagType: string, sTagIds: string[], dctWBTag:{[id: number]: WB_TagRow}): void {

        const self = this
        if(_.isEmpty(sTagIds)) {
            return;
        }

        let rowsToAdd: WB_TagRow[] = []

        _.forEach(srcTags, (aTag1: WB_TagRow) => {
            if (aTag1.cif.TAG_TYPE != sTagType) {
                return
            }

            if (_.indexOf(sTagIds, aTag1.cif.TAG_ID) != -1) {
                rowsToAdd.push(aTag1)
            }
        })

        _.forEach(rowsToAdd, (aTag2: WB_TagRow) => {
            self.addSelfAndChildren(aTag2, dctWBTag)
        })


    }

    addSelfAndChildren(pTag: WB_TagRow, dctWBTag:{[id: number]: WB_TagRow}) {

        if(_.isEmpty(pTag)){
            return
        }

        if (pTag.cif.WB_TAG_ID in dctWBTag) {
            return // already added, don't add children, already done.
        }

        dctWBTag[pTag.cif.WB_TAG_ID] = pTag

        let children: WB_TagRelationshipRow[] = (<any>pTag).Children

        if (_.isEmpty(children)) {
            return
        }

        if (!_.isArray(children)) {
            return
        }

        _.forEach(children, (aTagRel: WB_TagRelationshipRow) => {
            if(!aTagRel.isActive)
                return

            if (!_.isEmpty(aTagRel.WB_TagRowChild)) {
                this.addSelfAndChildren(aTagRel.WB_TagRowChild, dctWBTag)
            }
        })



    }


}
